import { RDMonogram, RDBadge, RDPrimary, RDHorizontal, RDMonoMark, NAVY, GOLD } from "@/components/brand/RDIdentity";
import { useSeo } from "@/hooks/useSeo";

const Section = ({ title, sub, children, dark = false }) => (
  <section className={`py-16 sm:py-20 ${dark ? "bg-navy" : "bg-white"}`}>
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <p className="eyebrow">{sub}</p>
      <h2 className={`mt-2 font-display text-2xl font-extrabold tracking-tight sm:text-3xl ${dark ? "text-white" : "text-navy"}`}>{title}</h2>
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

const Card = ({ children, label, dark = false, className = "" }) => (
  <figure className={`flex flex-col overflow-hidden rounded-2xl border ${dark ? "border-white/10 bg-navy" : "border-slate-200 bg-slate-50"} ${className}`}>
    <div className="flex flex-1 items-center justify-center p-10">{children}</div>
    <figcaption className={`border-t px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] ${dark ? "border-white/10 text-slate-400" : "border-slate-200 text-slate-500"}`}>
      {label}
    </figcaption>
  </figure>
);

export default function Brand() {
  useSeo("Brand Identity | RD Cleaning Services", "RD Cleaning Services brand identity — logo versions, colours, typography and applications.");
  return (
    <div className="min-h-screen bg-slate-100" data-testid="brand-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-6 sm:px-6">
          <div>
            <p className="eyebrow">Brand Identity v2.0</p>
            <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
              RD Cleaning Services
            </h1>
            <p className="mt-1 text-sm text-slate-500">Clean Today. Trusted Tomorrow.</p>
          </div>
          <a href="/" className="focus-ring rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-navy hover:border-teal-500 hover:text-teal-700" data-testid="brand-back-link">
            ← Back to Website
          </a>
        </div>
      </header>

      {/* Hero presentation */}
      <Section title="The Identity" sub="01 — Official Logo">
        <div className="grid gap-6 lg:grid-cols-2">
          <Card label="Primary Lockup — Light Applications">
            <RDPrimary size={320} />
          </Card>
          <Card label="Primary Lockup — Dark Applications" dark>
            <RDPrimary size={320} />
          </Card>
        </div>
        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-600">
          The official RD Cleaning Services identity: a bold navy RD monogram swept by a signature gold
          shine and a single spark — quality and polish without cleaning-equipment clichés. The artwork
          is presented on its dark field with a soft glow, so it is always used inside a clean rounded
          tile. A vector one-colour companion mark covers print, embroidery and stamps.
        </p>
      </Section>

      {/* Versions */}
      <Section title="Logo Versions" sub="02 — The System">
        <div className="grid gap-6 lg:grid-cols-2">
          <Card label="Version 1 — Primary (Full Lockup)">
            <RDPrimary size={280} />
          </Card>
          <Card label="Version 2 — Horizontal (Headers, Documents)">
            <RDHorizontal size={64} />
          </Card>
          <Card label="Version 3 — Monogram Mark">
            <RDMonogram size={260} />
          </Card>
          <Card label="Version 3b — Social / App Icon Sizes" dark>
            <div className="flex items-end gap-5">
              <RDBadge size={110} />
              <RDBadge size={64} />
              <RDBadge size={32} />
              <RDBadge size={16} />
            </div>
          </Card>
          <Card label="Version 4 — One-Colour Black (Print, Embroidery)">
            <RDMonoMark size={140} color="#000000" />
          </Card>
          <Card label="Version 4b — One-Colour White (Reversed)" dark>
            <RDMonoMark size={140} color="#FFFFFF" />
          </Card>
        </div>
        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-600">
          The full-colour artwork carries gradients and glow, so for one-colour jobs (embroidery, stamps,
          single-colour print) use the vector companion monogram — the same RD ligature idea, redrawn in
          flat geometry that survives any reproduction method.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {[
            ["/logo.png", "Primary Logo (PNG)"],
            ["/logo-mark.png", "Monogram Mark (PNG)"],
            ["/logo-favicon.png", "Icon (PNG)"],
            ["/brand/rd-monogram-black.svg", "One-Colour Black (SVG)"],
            ["/brand/rd-monogram-white.svg", "One-Colour White (SVG)"],
          ].map(([file, label]) => (
            <a key={file} href={file} download target="_blank" rel="noopener noreferrer" className="focus-ring rounded-full border border-slate-300 bg-white px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-navy transition-colors hover:border-teal-500 hover:text-teal-700" data-testid={`download-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              ↓ {label}
            </a>
          ))}
        </div>
      </Section>

      {/* Colours + Type */}
      <Section title="Colour & Typography" sub="03 — Foundations">
        <div className="grid gap-6 sm:grid-cols-4">
          {[
            { name: "Deep Navy", hex: NAVY, note: "Trust & authority", light: false },
            { name: "Signature Gold", hex: GOLD, note: "Quality & shine", light: false },
            { name: "Charcoal", hex: "#05080F", note: "Artwork field", light: false },
            { name: "White", hex: "#FFFFFF", note: "Clean space", light: true },
          ].map((c) => (
            <div key={c.name} className={`rounded-2xl border p-6 ${c.light ? "border-slate-300" : "border-transparent"}`} style={{ background: c.hex }}>
              <p className={`font-display text-lg font-bold ${c.light ? "text-navy" : "text-white"}`}>{c.name}</p>
              <p className={`font-mono text-xs ${c.light ? "text-slate-500" : "text-slate-300"}`}>{c.hex}</p>
              <p className={`mt-2 text-xs ${c.light ? "text-slate-500" : "text-slate-300"}`}>{c.note}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 grid gap-6 rounded-2xl border border-slate-200 bg-slate-50 p-8 sm:grid-cols-2">
          <div>
            <p className="font-display text-3xl font-extrabold tracking-tight text-navy">Plus Jakarta Sans</p>
            <p className="mt-1 text-sm text-slate-500">Display & wordmark — modern, established, highly readable.</p>
          </div>
          <div>
            <p className="font-mono text-xl font-semibold uppercase tracking-[0.3em] text-navy">JetBrains Mono</p>
            <p className="mt-1 text-sm text-slate-500">Supporting labels — precise, technical, clean.</p>
          </div>
        </div>
      </Section>

      {/* Applications */}
      <Section title="Applications" sub="04 — In The Real World" dark>
        <div className="grid gap-6 md:grid-cols-2">
          <Card label="Website Header" dark>
            <div className="w-full max-w-md rounded-xl bg-white px-5 py-3 shadow-xl">
              <div className="flex items-center justify-between">
                <RDHorizontal size={34} />
                <div className="hidden items-center gap-3 sm:flex">
                  <span className="h-2 w-10 rounded bg-slate-200" />
                  <span className="h-2 w-10 rounded bg-slate-200" />
                  <span className="rounded-full bg-teal-600 px-3 py-1.5 text-[10px] font-bold text-white">Get a Free Quote</span>
                </div>
              </div>
            </div>
          </Card>
          <Card label="Social Profile & Favicon" dark>
            <div className="flex items-end gap-5">
              <RDBadge size={110} />
              <RDBadge size={64} />
              <RDBadge size={32} />
              <RDBadge size={16} />
            </div>
          </Card>
          <Card label="Business Card — Front & Back" dark>
            <div className="flex flex-col gap-4">
              <div className="aspect-[1.7] w-72 rounded-lg bg-white p-5 shadow-2xl">
                <RDHorizontal size={30} />
                <div className="mt-6 space-y-1">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">+61 449 686 604</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Perth, Western Australia</p>
                </div>
              </div>
              <div className="flex aspect-[1.7] w-72 items-center justify-center rounded-lg bg-navy shadow-2xl ring-1 ring-white/20">
                <img src="/logo-mark.png" alt="RD monogram" className="h-14 w-auto rounded-md" />
              </div>
            </div>
          </Card>
          <Card label="Uniform — Polo Chest & Back" dark>
            <div className="flex items-center gap-8">
              <svg viewBox="0 0 200 200" className="h-44 w-44" aria-label="Polo shirt front with RD logo on chest">
                <path d="M60 30 L85 18 Q100 28 115 18 L140 30 L172 58 L152 82 L142 72 L142 182 L58 182 L58 72 L48 82 L28 58 Z" fill="#FFFFFF" />
                <path d="M85 18 Q100 28 115 18 L112 30 Q100 38 88 30 Z" fill="#E2E8F0" />
                <image href="/logo-mark.png" x="112" y="66" width="34" height="17" preserveAspectRatio="xMidYMid meet" />
              </svg>
              <svg viewBox="0 0 200 200" className="h-44 w-44" aria-label="Polo shirt back with RD logo">
                <path d="M60 30 L85 18 Q100 28 115 18 L140 30 L172 58 L152 82 L142 72 L142 182 L58 182 L58 72 L48 82 L28 58 Z" fill="#FFFFFF" />
                <image href="/logo.png" x="45" y="80" width="110" height="72" preserveAspectRatio="xMidYMid meet" />
              </svg>
            </div>
          </Card>
          <Card label="Commercial Vehicle Signage" dark className="md:col-span-2">
            <svg viewBox="0 0 640 240" className="w-full max-w-2xl" aria-label="Van side with RD Cleaning Services livery">
              <path d="M40 60 Q40 40 60 40 L380 40 L440 90 L560 105 Q600 110 600 150 L600 180 Q600 195 585 195 L60 195 Q40 195 40 175 Z" fill="#FFFFFF" />
              <path d="M385 48 L432 90 L385 90 Z" fill="#DCE7F5" />
              <rect x="392" y="48" width="6" height="140" fill="#F1F5F9" />
              <path d="M40 150 L600 150 L600 170 Q600 195 585 195 L60 195 Q40 195 40 175 Z" fill={NAVY} />
              <circle cx="150" cy="195" r="26" fill="#0F172A" />
              <circle cx="150" cy="195" r="11" fill="#94A3B8" />
              <circle cx="500" cy="195" r="26" fill="#0F172A" />
              <circle cx="500" cy="195" r="11" fill="#94A3B8" />
              <image href="/logo-mark.png" x="66" y="58" width="150" height="75" preserveAspectRatio="xMidYMid meet" />
              <text x="230" y="96" fontFamily="'Plus Jakarta Sans', Arial, sans-serif" fontWeight="800" fontSize="26" fill={NAVY} letterSpacing="0.5">RD Cleaning Services</text>
              <text x="232" y="122" fontFamily="'JetBrains Mono', monospace" fontWeight="600" fontSize="10" fill={GOLD} letterSpacing="3.5">CLEAN TODAY. TRUSTED TOMORROW.</text>
              <text x="232" y="172" fontFamily="'JetBrains Mono', monospace" fontWeight="500" fontSize="11" fill="#FFFFFF" letterSpacing="2">+61 449 686 604</text>
            </svg>
          </Card>
        </div>
      </Section>

      {/* Rules */}
      <Section title="Usage Rules" sub="05 — Keep It Strong">
        <div className="grid gap-6 lg:grid-cols-2">
          <Card label="Clear Space & Minimum Size">
            <div className="space-y-4 text-left text-sm text-slate-600">
              <p>✓ Clear space around the tile: at least the height of the spark on all sides</p>
              <p>✓ Minimum digital size: 32px height · print: 10mm</p>
              <p>✓ Always keep the rounded tile — never crop into the glow</p>
            </div>
          </Card>
          <Card label="Misuse">
            <div className="space-y-4 text-left text-sm text-slate-600">
              <p>✗ Don't stretch, recolour or add effects to the artwork</p>
              <p>✗ Don't place the glow artwork on busy photography without the tile</p>
              <p>✗ Don't use the full-colour artwork for one-colour jobs — use the vector companion mark</p>
            </div>
          </Card>
        </div>
      </Section>

      <footer className="border-t border-slate-200 bg-white py-8 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
        RD Cleaning Services — Brand Identity · Clean Today. Trusted Tomorrow.
      </footer>
    </div>
  );
}
